import * as React from 'react'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import { getSubtaskElements } from '../../../app-core/lib/flow/element'
import type { SubtaskReport } from '../../../app-core/lib/flow/ElementSubtask'
import type { Report, Props as ExecutionPageProps } from '../../../app-core/lib/flow/ExecutionPage'
import { ExecutionPageWithGuides } from '../../../app-core/lib/flow/ExecutionPageWithGuides'
import { createContinuationId, type FlowContinuationId } from '../../../app-core/lib/flow/FlowReport'
import type { ParentInfo, WorkflowAppGuide } from '../../../app-core/lib/guide/Model'
import { isFlowWorkflow } from '../../../app-core/lib/workflow/Workflow'
import { Rout_toUnitContentPage } from '../../../app/zap/Rout/Rout.unit'
import { Rout_toTodo, Rout_toOpenFlowReportSummary } from '../Rout/Rout.todo'
import { RelayState } from '../../../shared/domain/auth'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { fromLoopPath, type LoopPath, type LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import type { ElementId, Flow, ReportId as FlowReportId } from '../../../shared/domain/flows/types.no-deps'
import type { Id as GuideId, SyncGuide } from '../../../shared/domain/guides/types.no-deps'
import type { CompactReportGetOutput, ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { filter, map } from '../../../shared/lib/functional/array.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { arrayToMap } from '../../../shared/lib/helpers'
import { strictParseFloat, strictParseInt } from '../../../shared/lib/number.no-deps'
import type { NominalUUID, Nullable, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import * as sentry from '../../src/utils/sentry'
import { Auth_redirectSign } from '../Auth/Auth.navigate'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import type { Base_IOperations } from '../Base/Base.IContext'
import { Base_Component } from '../Base/Base.ui.Component'
import { Cust_hasFeature } from '../Cust/Cust.hasFeature'
import { Devi_readCameraBrowserApi } from '../Devi/Devi.cameraBrowserApi'
import { DS_getData, DS_setData } from '../DS/DS.Data'
import { E2E_dispatchReportInvalid } from '../E2E/E2E.dispatch'
import {
    getGuideElementsContextMap,
    getRequiredGuideElementsMap,
    Guid_downloadFile,
    Guid_fetchAppGuides,
    Guid_fetchFileGuideDetailsMap,
    Guid_fetchGuideParentInfo,
    Guid_fetchReportGuideDetails,
    Guid_fetchSyncGuidesByIds,
    Guid_fetchSyncGuidesByTagIds,
    Guid_fetchWorkflowGuideReportId,
    Guid_routeToGuideParentPage,
} from '../Guid/Guid.helpers'
import {
    IDB_browserCameraDeviceIndex,
    IDB_flowActionReports,
    IDB_flowAnswers,
    IDB_flowContinuation,
    IDB_flowReports,
    IDB_procedures,
    IDB_scheduledInstances,
} from '../IDB/IDB.dexie'
import { IDB_unitTypeRead } from '../IDB/IDB.table.unitType'
import { Imag_renderCachedImage } from '../Imag/Imag.ui.CachedImage'
import { Imag_withCachedImageUrl } from '../Imag/Imag.ui.withCachedImageUrl'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { Navi_bottomBar, Navi_setTodoSubpage } from '../Navi/Navi.ui.BottomBar'
import { Plat_clearRecoveryValue, Plat_getRecoveryValue, Plat_savePathForRecovery } from '../Plat/Plat.androidRecovery'
import { Plat_cordovaGetMedia } from '../Plat/Plat.cordovaGetMedia'
import { Plat_isAndroid, Plat_isCordova, Plat_isIOS } from '../Plat/Plat.device'
import { Rout_getPath, Rout_replace, reloadPage } from '../Rout/Rout'
import { Rout_toFlowExecutionPage, Rout_toFlowExecutionPagePath } from '../Rout/Rout.flow'
import { Rout_toGuideExecutionPage } from '../Rout/Rout.guide'
import { Rout_toHome } from '../Rout/Rout.home'
import { Rout_toProcedureContentPage } from '../Rout/Rout.procedure'
import { Serv_client } from '../Serv/Serv.client'
import { isOnline } from '../Serv/Serv.socketClient'
import { Unit_barcodeScanForUnits } from '../Unit/Unit.codeLookup'
import {
    Unit_fetchById,
    Unit_fetchByIds,
    Unit_fetchForTypes,
    UnitType_fetchById,
    UnitType_fetchNameById,
} from '../Unit/Unit.fetch'
import { appUploadQueue } from '../Uplo/AppUploadQueue'
import { readBlob } from '../Uplo/read'
import { saveBlob } from '../Uplo/saveBlob'
import { saveFile } from '../Uplo/saveFile'
import { selectFile } from '../Uplo/selectFile'
import { User_get } from '../User/User.get'
import { User_profilesFetchExisting } from '../User/User.profiles'
import { getFlagValue } from '../Util/Util.launchdarkly'
import { WF_fetch, WF_fetchByIds } from '../WF/WF.fetch'
import { Flow_createAnswerBase } from './Flow.answer'
import {
    Flow_cancelReport,
    Flow_createSubTask,
    Flow_fetchAnswers,
    type Flow_FetchedReport,
    Flow_fetchFilesMetaData,
    Flow_fetchReport,
    Flow_fetchSubtaskReports,
    Flow_navigateToDoneReport,
    Flow_reportDone,
    Flow_saveTitle,
    Flow_saveUnitSubject,
    markReportAsCompleted,
    markReportAsIncomplete,
    Flow_fetchReportHistory,
    Flow_prepareReportForCompletion,
} from './Flow.Report'
import { Flow_syncOngoingReport } from './Flow.syncOngoingReports'
import { Flow_ReauthModals, Flow_ReAuthModalType } from './Flow.ui.ReauthModals'
import { Flow_updateSubscriber } from './Flow.updateSubscriber'
import { tryUploadNewReport, uploadNewReport } from './Flow.uploadNewReports'
import { Flow_saveFeedback } from './Flow.Feedback'
import { flatten } from '../../../shared/lib/array.no-deps'
import { Rpts_fetchReportData, Rpts_viewReportFetch } from '../Rpts/Rpts.viewReportFetch'
import { Rout_toPost } from '../Rout/Rout.post'
import type { AiChatUserContext } from '../../../shared/domain/aiChats/types.no-deps'
import type { Flow_ActionReport } from './Flow.ActionReport'
import {
    Todo_canCurrentUserSeeOriginatingReport,
    Todo_isSimpleAction,
    type Todo_ScheduledInstanceDPO,
} from '../Todo/Todo.ScheduledInstance'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import { Rout_toReportView } from '../Rout/Rout.report'
import { isRestrictedUser } from '../../../shared/domain/users/permissions'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'

interface Flow_ExecutionPageProps {
    params: {
        reportId: FlowReportId
    }
    location: {
        query: {
            scrollTop?: string
            sectionId?: ElementId
            scrollToElementId?: ElementId
            scrollToLoopPath?: LoopPath
            comingFromWorkflow?: string
            comingFromUnit?: string
            comingFromSubTask?: FlowReportId
            comingFromScheduledInstance?: UUID
            comingFromActionScheduledInstance?: string
            elementToRecover?: ElementId
            loopPath?: LoopPath
            relayState?: RelayState
            success?: string
            signature?: string
            signedAtTime?: string
            comment?: UUID
        }
    }
}

interface Flow_ExecutionPageState {
    visibleReAuthModal: Flow_ReAuthModalType
    featureFlags: Pick<CamelCaseFlags, 'appendToReportTitle'>
}

export class Flow_ExecutionPage extends Base_Component<Flow_ExecutionPageProps, Flow_ExecutionPageState> {
    public operations: Base_IOperations
    public state: Flow_ExecutionPageState = {
        visibleReAuthModal: null,
        featureFlags: {
            appendToReportTitle: false,
        },
    }

    private getParams = () => {
        const { reportId } = this.props.params
        const {
            scrollTop,
            sectionId,
            comingFromWorkflow,
            comingFromUnit,
            comingFromSubTask,
            comingFromScheduledInstance,
            comingFromActionScheduledInstance,
            loopPath,
            scrollToElementId,
            scrollToLoopPath,
            comment,
        } = this.props.location.query

        return {
            reportId,
            scrollTop,
            sectionId,
            scrollToElementId,
            scrollToLoopPath,
            comingFromWorkflow: comingFromWorkflow && strictParseInt(comingFromWorkflow),
            comingFromUnit: comingFromUnit && strictParseInt(comingFromUnit),
            comingFromSubTask,
            comingFromScheduledInstance,
            comingFromActionScheduledInstance:
                comingFromActionScheduledInstance && comingFromActionScheduledInstance === 'true',
            loopPathIds: loopPath ? fromLoopPath(loopPath) : [],
            comment,
        }
    }

    private routeToReturnHere = () => {
        const { reportId, scrollTop, sectionId, comingFromSubTask } = this.getParams()
        Rout_toFlowExecutionPage(reportId, { scrollTop: strictParseInt(scrollTop), sectionId, comingFromSubTask })
    }

    private maybeFinalizeReport = async (
        logger: ClientLogger,
        query: Partial<Flow_ExecutionPageProps['location']['query']>
    ) => {
        const { signature, relayState, success, signedAtTime } = query
        const { reportId } = this.getParams()

        if (relayState !== RelayState.SignReport) {
            return
        }
        reloadPage()
        if (success === 'false') {
            this.setState({ visibleReAuthModal: Flow_ReAuthModalType.UnexpectedError })
            await markReportAsIncomplete(logger, reportId)
            return
        }

        if (success === 'true') {
            const { flowReport, post } = await Flow_prepareReportForCompletion(reportId, this.state.featureFlags)
            try {
                await Flow_syncOngoingReport(logger, reportId, signature, new Date(signedAtTime))
            } catch (error) {
                this.setState({ visibleReAuthModal: Flow_ReAuthModalType.UnexpectedError })

                await markReportAsIncomplete(logger, reportId)
                throw error
            }

            await Flow_navigateToDoneReport(reportId, this.operations.getBus(), flowReport, post)
        }
    }

    private logger = Logg_createLogger(`Flow_ExecutionPage.maybeFinalizeReport`)

    public componentDidMount = async () => {
        const isAppendToReportTitleEnabled = getFlagValue('append-to-report-title', ENV.isTest)
        const featureFlags = { appendToReportTitle: isAppendToReportTitleEnabled }
        this.setState({ featureFlags })

        this.maybeFinalizeReport(this.logger, this.props.location.query)
        Navi_setTodoSubpage(this.routeToReturnHere)
    }

    public componentDidUpdate() {
        this.maybeFinalizeReport(this.logger, this.props.location.query)
        Navi_setTodoSubpage(this.routeToReturnHere)
    }

    private createContinuation = async (): Promise<FlowContinuationId> => {
        const { reportId, sectionId } = this.getParams()
        const continuationId = createContinuationId()

        // Not ideal, but easiest to query for element directly
        const scrollTop = document.querySelector('.AppCore__page__Page__body')?.scrollTop ?? 0

        // Save information to continue the report for later
        await IDB_flowContinuation().set({
            continuationId,
            reportId,
            sectionId,
            scrollTop,
        })

        return continuationId
    }

    private async navigateToReportSigning(reportClientId: ReportClientId) {
        const logger = Logg_createLogger(`Flow_renderExecutionPage.signReport`)

        if (!(await isOnline())) {
            this.setState({ visibleReAuthModal: Flow_ReAuthModalType.OfflineError })
            return
        }

        try {
            this.setState({ visibleReAuthModal: null })
            await uploadNewReport(logger, reportClientId)
            await Flow_syncOngoingReport(logger, reportClientId)
            const reportHash = await Serv_client.getReportHash(reportClientId).then((body) => body.hash)
            await Auth_redirectSign(
                reportHash,
                Rout_getPath(),
                User_get().email,
                RelayState.SignReport,
                RelayState.ReportSigningCancelled
            )
        } catch (error) {
            this.setState({ visibleReAuthModal: Flow_ReAuthModalType.UnexpectedError })
            await markReportAsIncomplete(logger, reportClientId)
            throw error
        }
    }

    public tryRender() {
        const {
            reportId,
            sectionId,
            comingFromWorkflow,
            comingFromUnit,
            comingFromSubTask,
            comingFromScheduledInstance,
            comingFromActionScheduledInstance,
            loopPathIds,
            scrollToElementId,
            scrollToLoopPath,
            comment,
        } = this.getParams()
        const { query } = this.props.location
        const scrollTop = query.scrollTop ? strictParseFloat(query.scrollTop) : null
        const { customer } = DS_getData()

        return (
            <div className="Flow_ExecutionPage">
                <Flow_ReauthModals
                    visibleReAuthModal={this.state.visibleReAuthModal}
                    onCancel={() => {
                        removeMaybeFaultyQuery()
                        this.setState({ visibleReAuthModal: null })
                    }}
                    navigateToReportSigning={() => this.navigateToReportSigning(reportId)}
                    unexpectedErrorMessage={Translations.t(
                        'shared:Reauthentication.unexpectedError.onReportSubmission'
                    )}
                    offlineMessage={Translations.t('shared:Reauthentication.offlineError')}
                    confirmationMessage={Translations.t('shared:Reauthentication.confirmation.onReportSubmission')}
                />
                <Flow_renderExecutionPage
                    reportId={reportId}
                    sectionId={sectionId}
                    scrollToElementId={scrollToElementId}
                    scrollToLoopPath={scrollToLoopPath}
                    loopPathIds={loopPathIds}
                    fetchReport={fetchReport}
                    routeBack={() =>
                        Rout_toOpenFlowReportSummary(reportId, {
                            comingFromActionScheduledInstance,
                            comingFromScheduledInstance,
                            comingFromUnit,
                            comingFromWorkflow,
                        })
                    }
                    routeToReturnHere={this.routeToReturnHere}
                    routeToExecutionPage={Rout_toFlowExecutionPage}
                    routeToPostPage={(postId: UUID) =>
                        Rout_toPost(postId as NominalUUID<'TAppPostId'>, this.routeToReturnHere)
                    }
                    routeToReportPage={(reportId: number) => Rout_toReportView(reportId)}
                    onSubmit={() =>
                        Flow_reportDone(reportId, this.operations.getBus(), this.state.featureFlags, {
                            comingFromActionScheduledInstance,
                            comingFromScheduledInstance,
                            comingFromUnit,
                            comingFromWorkflow,
                        })
                    }
                    onCancel={async () => {
                        await Flow_cancelReport(this.props.params.reportId)
                        Rout_toTodo()
                    }}
                    footer={Navi_bottomBar('todo', this.createContinuation)}
                    initialScrollTop={scrollTop}
                    comingFromActionScheduledInstance={comingFromActionScheduledInstance}
                    comingFromScheduledInstance={comingFromScheduledInstance}
                    comingFromSubTask={comingFromSubTask}
                    comingFromUnit={comingFromUnit}
                    comingFromWorkflow={comingFromWorkflow}
                    customer={customer.id}
                    savePathForRecovery={async (elementToRecover, scrollTop = 0) => {
                        const path = Rout_toFlowExecutionPagePath(reportId, {
                            scrollTop,
                            sectionId,
                            elementToRecover,
                            comingFromActionScheduledInstance,
                            comingFromScheduledInstance,
                            comingFromUnit,
                        })
                        await Plat_savePathForRecovery(path)
                    }}
                    recovery={this.getRecovery()}
                    sign={() => this.showSignConfirmationDialog()}
                    comment={comment}
                />
            </div>
        )
    }

    async showSignConfirmationDialog() {
        const visibleReAuthModal = (await Base_commonDeps().isOnline())
            ? Flow_ReAuthModalType.Confirmation
            : Flow_ReAuthModalType.OfflineError
        this.setState({ visibleReAuthModal })
    }

    private getRecovery() {
        const recoveryValue = Plat_getRecoveryValue()
        const { elementToRecover } = this.props.location.query
        if (elementToRecover == null || recoveryValue == null) return null
        return { elementId: elementToRecover, value: recoveryValue, clear: Plat_clearRecoveryValue }
    }
}

export const fetchReport = async (reportClientId: FlowReportId): Promise<Report> => {
    const logger = Logg_createLogger(`Flow_ExecutionPage.fetchReport`)

    const report = await Flow_fetchReport(logger, reportClientId)
    if (report == null) return null

    logger.log(`fetching answers`, { reportClientId })
    const flowAnswers = await Flow_fetchAnswers(report.id)

    logger.log(`fetching subtasks`, { reportClientId })
    const subtasksReports = await Flow_fetchSubtaskReports(logger, flowAnswers)

    const teams = report.scheduledInstance?.teams?.map((team) => team.name)

    const requiresReauthentication =
        report.workflow.requiresSignature ||
        (isNotNil(report.scheduledInstance)
            ? ((await IDB_scheduledInstances().get(report.scheduledInstance.id))?.origin?.requiresSignature ?? false)
            : false)

    const executionReport: Report = {
        ...report,
        openState: report.status,
        flow: report.workflow.flow,
        flowTitle: report.title,
        subtaskReports: pipe(subtasksReports, map(mapToSubtaskReport), (a) => arrayToMap(a, 'id')),
        subjectUnitData: report.subjectUnitData ?? null,
        subjectUnitVersion: report.subjectUnitVersion ?? null,
        loopPlan: report.loopPlan ?? [],
        cachedUnitData: report.cachedUnitData ?? [],
        type: report.workflow.type,
        dueDate: report.scheduledInstance?.dueDate,
        assignee: { teams },
        procedure: report.procedure,
        workflowName: report.workflow.name,
        workflowRequiresSignature: requiresReauthentication,
        originId: report.origin,
        scheduledInstanceId: report.scheduledInstance?.id,
        scheduleParameters: report.scheduledInstance
            ? ((await IDB_scheduledInstances().get(report.scheduledInstance?.id))?.parameters ?? [])
            : [],
    }

    if (report.parentClientId) {
        const parentReport = await Flow_fetchReport(logger, report.parentClientId)
        const progenitor = await IDB_flowReports().get(report.progenitorClientId)

        return {
            ...executionReport,
            parentClientId: parentReport.id,
            parentReportTitle: parentReport.title ?? parentReport.workflow.name,
            parentSubjectUnitId: parentReport.subjectUnit.id,
            progenitorOpenState: progenitor?.status,
        }
    }

    return executionReport
}

const hasAccessToProcedure = async (procedureId: number, deps: CommonDeps): Promise<boolean> => {
    if (!getFlagValue('procedure-bubbles', false)) {
        return true
    }

    if (!isRestrictedUser(deps.user?.privs)) {
        return true
    }

    const procedure = await IDB_procedures().get(procedureId)

    return procedure?.isAssigned
}

/**
 * Shared between the flow execution page and the activity execution page
 */

type Flow_renderExecutionPageProps = {
    reportId: FlowReportId
    guideElementId?: ElementId
    guideId?: GuideId
    sectionId: ElementId
    loopPathIds: LoopPathIds
    startTimestamp?: Date
    fetchReport: (reportId: FlowReportId) => Promise<Report>
    afterWriteAnswer?: () => void
    routeBack: () => void
    routeToReturnHere: () => void
    routeToPostPage: (postId: UUID) => void
    routeToReportPage: (reportId: number) => void
    routeToExecutionPage: (
        reportId: FlowReportId,
        opts?: {
            scrollTop?: number
            sectionId: ElementId
            comingFromActionScheduledInstance?: boolean
            comingFromScheduledInstance?: UUID
            comingFromSubTask?: FlowReportId
            comingFromUnit?: number
            comingFromWorkflow?: number
            loopPathIds?: LoopPathIds
        }
    ) => void
    onSubmit: () => Promise<void>
    onCancel: () => void
    headerExtra?: React.ReactElement<unknown>
    footer?: React.ReactElement<unknown>
    initialScrollTop?: number
    comingFromActionScheduledInstance?: boolean
    comingFromScheduledInstance?: UUID
    comingFromSubTask?: FlowReportId
    comingFromUnit?: number
    comingFromWorkflow?: number
    customer: number
    savePathForRecovery?: (elementToRecover: ElementId, scrollTop: number) => Promise<void>
    recovery?: { elementId: ElementId; value: unknown; clear: () => void }
    sign?: (reportClientId: ReportClientId) => void
    scrollToElementId?: ElementId
    scrollToLoopPath?: LoopPath
    parentScrollTop?: number
    showHeader?: boolean
    allowFeedbackSubmission?: boolean
    comment?: UUID
}

interface Flow_renderExecutionPageState {
    isNewViewReportPageEnabled: boolean
    hasWorkflow: boolean
    isReportsTranslationEnabled: boolean
    isAppendToReportTitleEnabled: boolean
    isReportCommentsTranslationEnabled: boolean
    isWorkflowFeedbackV2Enabled: boolean
    useNewElementNumberInput: boolean
    reportPostId: UUID | null
}

export class Flow_renderExecutionPage extends Base_Component<
    Flow_renderExecutionPageProps,
    Flow_renderExecutionPageState
> {
    public state: Flow_renderExecutionPageState = {
        isNewViewReportPageEnabled: false,
        hasWorkflow: false,
        isReportsTranslationEnabled: false,
        isAppendToReportTitleEnabled: false,
        isReportCommentsTranslationEnabled: false,
        isWorkflowFeedbackV2Enabled: false,
        useNewElementNumberInput: false,
        reportPostId: null,
    }

    public componentDidMount = async () => {
        const logger = Logg_createLogger('Flow_renderExecutionPage.componentDidMount')
        const { reportId: reportClientId } = this.props

        const isNewViewReportPageEnabled = getFlagValue('new-view-report-page', false)
        const isReportsTranslationEnabled = getFlagValue('reports-translation', ENV.isTest)
        const isReportCommentsTranslationEnabled = getFlagValue('report-comments-translation', ENV.isTest)
        const isAppendToReportTitleEnabled = getFlagValue('append-to-report-title', ENV.isTest)
        const isWorkflowFeedbackV2Enabled = getFlagValue('workflow-feedback-v2', ENV.isTest)
        const useNewElementNumberInput = getFlagValue('use-new-element-number-input', ENV.isTest)

        // Try to upload report right away to allow commenting
        await tryUploadNewReport(logger, this.props.reportId)
        await this.updateReportPostId(logger)

        const report = await Flow_fetchReport(logger, reportClientId)
        const hasWorkflow = !!report?.workflow

        this.setState({
            hasWorkflow,
            isNewViewReportPageEnabled,
            isReportsTranslationEnabled,
            isReportCommentsTranslationEnabled,
            isAppendToReportTitleEnabled,
            isWorkflowFeedbackV2Enabled,
            useNewElementNumberInput,
        })
    }

    public async componentDidUpdate(prevProps: Flow_renderExecutionPageProps) {
        const logger = Logg_createLogger('Flow_renderExecutionPage.componentDidUpdate')
        if (prevProps.reportId !== this.props.reportId) {
            // Try to upload report right away to allow commenting
            await tryUploadNewReport(logger, this.props.reportId)
            await this.updateReportPostId(logger)
        }
    }

    public tryRender() {
        const props = this.props
        const deps = Base_commonDeps()

        const submitFeedback = async (elementId: ElementId, message: string, loopPath?: LoopPath) =>
            Flow_saveFeedback(elementId, message, this.props.reportId, loopPath)

        const featureFlags: ExecutionPageProps['featureFlags'] = {
            reportsTranslation: this.state.isReportsTranslationEnabled,
            reportCommentsTranslation: this.state.isReportCommentsTranslationEnabled,
            workflowFeedbackV2: this.state.isWorkflowFeedbackV2Enabled,
            appendToReportTitle: this.state.isAppendToReportTitleEnabled,
            useNewElementNumberInput: this.state.useNewElementNumberInput,
        }

        return (
            <ExecutionPageWithGuides
                key={
                    // the reports are fetched when mounting this component,
                    // so force a remount when we change report
                    props.reportId
                }
                deps={deps}
                createAnswerBase={(elementId, loopPathIds, oldAnswer, _, dynamicElement) =>
                    Flow_createAnswerBase(props.reportId, elementId, loopPathIds, oldAnswer, dynamicElement)
                }
                reportId={props.reportId}
                reportPostId={this.state.reportPostId}
                guideElementId={props.guideElementId}
                guideId={props.guideId}
                fetchWorkflowVersion={fetchWorkflowVersion}
                sectionId={props.sectionId}
                loopPathIds={props.loopPathIds}
                startTimestamp={props.startTimestamp}
                featureFlags={featureFlags}
                fetchReport={props.fetchReport}
                fetchReportHistory={async (report) => fetchReportHistory(deps, report)}
                fetchAnswers={Flow_fetchAnswers}
                fetchFilesMetaData={() => Flow_fetchFilesMetaData(props.reportId)}
                fetchUnit={Unit_fetchById}
                fetchUnitType={UnitType_fetchById}
                fetchAllUnits={Unit_fetchByIds}
                fetchUnitTypeName={UnitType_fetchNameById}
                fetchUserProfiles={User_profilesFetchExisting}
                fetchRequiredGuidesMap={(logger, flow) => getRequiredGuideElementsMap(logger, flow)}
                fetchSubtask={(logger, reportClientId) =>
                    Flow_fetchReport(logger, reportClientId).then(mapToSubtaskReport)
                }
                fetchGuideElementsContextMap={(logger, flow) => getGuideElementsContextMap(logger, flow)}
                fetchOriginatingReportsCount={fetchOriginatingReportsCount}
                fetchOriginViewReportData={async (scheduledInstanceId: UUID) => {
                    if (!this.state.isNewViewReportPageEnabled) {
                        return null // The 'Originating report' widget uses the new `ViewReport` component.
                    }

                    const scheduledInstance = await IDB_scheduledInstances().get(scheduledInstanceId)
                    if (!scheduledInstance?.origin) {
                        return null
                    }

                    // Try to fetch the origin report from the server if report answers is missing.
                    // Scheduled instances synced before this are missing the `origin.answers` and `origin.title` data.
                    if (deps.isOnline()) {
                        const reportData = await Rpts_fetchReportData(scheduledInstance.origin.report)

                        return Rpts_viewReportFetch(reportData, null)
                    }

                    const reportData: CompactReportGetOutput = {
                        id: scheduledInstance.origin.report,
                        title: scheduledInstance.origin.title,
                        clientId: scheduledInstance.origin.reportClientId,
                        answers: scheduledInstance.origin.answers,
                        workflow: scheduledInstance.origin.workflow,
                        workflowVersion: scheduledInstance.origin.workflowVersion,
                        subjectUnitId: scheduledInstance.origin.subjectUnitId,
                        reportDetails: scheduledInstance.origin.reportDetails,
                        photoUrls: scheduledInstance.origin.photoUrls,
                        cachedUnitData: [],
                    }

                    return Rpts_viewReportFetch(reportData, null)
                }}
                writeAnswer={async (answer) => {
                    await IDB_flowAnswers().put({ ...answer, uploadState: 'pendingUpload' })

                    if (props.afterWriteAnswer != null) {
                        props.afterWriteAnswer()
                    }
                }}
                removeAnswers={async (answers) => {
                    await IDB_flowAnswers().bulkDelete(answers.map((r) => r.id))
                }}
                reportUpdate={{
                    subscribe: (fn) => Flow_updateSubscriber.subscribe(fn),
                    unsubscribe: (fn) => Flow_updateSubscriber.unsubscribe(fn, sentry.simpleError),
                }}
                routeBack={props.routeBack}
                routeToPostPage={props.routeToPostPage}
                routeToReportPage={props.routeToReportPage}
                routeToExecutionPage={props.routeToExecutionPage}
                routeToCompletedPage={async () => {
                    // For now just go back to home page, in the future we may improve
                    Rout_toHome(false)
                }}
                routeToGuideExecutionPage={Rout_toGuideExecutionPage}
                onSubmit={props.onSubmit}
                onCancel={props.onCancel}
                renderCachedImage={Imag_renderCachedImage}
                onClickGuideParentInfo={(parentInfo: ParentInfo) =>
                    Guid_routeToGuideParentPage(parentInfo, () =>
                        Rout_toFlowExecutionPage(props.reportId, { sectionId: props.sectionId })
                    )
                }
                cordovaGetMedia={
                    Plat_isCordova()
                        ? (uploadSource, mediaType) => Plat_cordovaGetMedia(uploadSource, 720, mediaType)
                        : null
                }
                headerExtra={props.headerExtra}
                footer={props.footer}
                fetchUploads={appUploadQueue.getReportUploads}
                subscribeToUploads={appUploadQueue.subscribeToGroup}
                unsubscribeToUploads={appUploadQueue.unsubscribeToGroup}
                selectFile={(fileExtensions, isIOS?: boolean, type?: 'image' | 'video') =>
                    selectFile(props.reportId, fileExtensions, props.customer, isIOS, type)
                }
                saveBlob={(blob) => saveBlob(props.reportId, blob, props.customer)}
                saveFile={(fileId: FileId, filePath: string) =>
                    saveFile(fileId, props.reportId, filePath, props.customer)
                }
                readBlob={readBlob}
                cancelUpload={appUploadQueue.cancelUpload}
                showFileProgress={false}
                withCachedImageUrl={Imag_withCachedImageUrl}
                routeForReportSubject={(subjectUnitId) =>
                    Rout_toUnitContentPage(subjectUnitId, {}, () =>
                        props.routeToExecutionPage(props.reportId, { sectionId: props.sectionId })
                    )
                }
                hasTouchKeyboard={Plat_isCordova()}
                initialScrollTop={props.initialScrollTop}
                fetchUnitsByIds={Unit_fetchByIds}
                fetchUnitsByType={Unit_fetchForTypes}
                saveUnitSubject={Flow_saveUnitSubject}
                routeToProcedurePage={(procedureId) =>
                    Rout_toProcedureContentPage(procedureId, props.routeToReturnHere)
                }
                createSubtask={Flow_createSubTask}
                deleteSubtask={Flow_cancelReport}
                comingFromActionScheduledInstance={props.comingFromActionScheduledInstance}
                comingFromScheduledInstance={props.comingFromScheduledInstance}
                comingFromSubTask={props.comingFromSubTask}
                comingFromUnit={props.comingFromUnit}
                comingFromWorkflow={props.comingFromWorkflow}
                markReportAsCompleted={markReportAsCompleted}
                markReportAsIncomplete={markReportAsIncomplete}
                fetchWorkflows={fetchSubtaskWorkflows}
                saveTitle={Flow_saveTitle}
                barcodeScanForUnits={{
                    scan: Unit_barcodeScanForUnits,
                    fetchUnitType: IDB_unitTypeRead,
                }}
                isIOS={Plat_isIOS()}
                isAndroid={Plat_isAndroid()}
                browserCameraDeviceIndexLookup={IDB_browserCameraDeviceIndex()}
                savePathForRecovery={props.savePathForRecovery}
                recovery={props.recovery}
                forceUseBrowserCameraAPI={Devi_readCameraBrowserApi().then(({ forceUseBrowser }) => forceUseBrowser)}
                dispatchReportInvalid={E2E_dispatchReportInvalid}
                featureEnabled={Cust_hasFeature}
                fetchFileGuideDetailsMap={(guides) => Guid_fetchFileGuideDetailsMap(deps, guides)}
                fetchGuideParentInfo={Guid_fetchGuideParentInfo}
                fetchReportGuideDetails={(reportGuideId) => Guid_fetchReportGuideDetails(reportGuideId)}
                fetchAppGuides={(syncGuides, withparentInfo) => fetchAppGuides(deps, syncGuides, withparentInfo)}
                fetchSyncGuides={fetchSyncGuides}
                fetchGuideReportId={(guide, parentClientId) => fetchGuideReportId(deps, guide, parentClientId)}
                onOpenFile={Guid_downloadFile}
                onChangeZapBotContext={(context: Nullable<AiChatUserContext>) =>
                    DS_setData('ZapBotContextChanged', () => ({ zapBotContext: context }))
                }
                showHeader={this.props.showHeader ?? true}
                allowImageZoom={true}
                sign={this.props.sign}
                scrollToElementId={this.props.scrollToElementId}
                scrollToLoopPath={this.props.scrollToLoopPath}
                parentScrollTop={this.props.parentScrollTop}
                submitFeedback={
                    this.props.allowFeedbackSubmission !== false && this.state.hasWorkflow && submitFeedback
                }
                hasAccessToProcedure={(procedureId) => hasAccessToProcedure(procedureId, deps)}
                comment={this.props.comment}
            />
        )
    }

    private async updateReportPostId(logger: ClientLogger) {
        const report = await Flow_fetchReport(logger, this.props.reportId)
        const reportPostId = report?.postId
        this.setState({ reportPostId })
    }
}

const fetchWorkflowVersion = (workflowId: number): Promise<number> =>
    WF_fetch(Logg_createLogger('Flow_renderExecutionPage.fetchWorkflowVersion'), workflowId, {
        includeDeleted: false,
    }).then((workflow) => workflow.numberOfVersions)

const fetchSubtaskWorkflows = async (flow: Flow) => {
    const logger = Logg_createLogger(`Flow_renderExecutionPage.fetchSubtaskWorkflows`)
    // We want the workflows for displaying subtasks, all the names would be a
    // lot of data so filter to just what we use
    const workflowIds = getSubtaskElements(flow).map((element) => element.workflow)
    return WF_fetchByIds(logger, workflowIds).then(filter(isFlowWorkflow))
}

const fetchAppGuides = (deps: CommonDeps, syncGuides: SyncGuide[], withParentInfo: boolean) =>
    Guid_fetchAppGuides(deps, Logg_createLogger(`Flow_renderExecutionPage.fetchAppGuides`), syncGuides, withParentInfo)

const fetchSyncGuides = async (guideIds: GuideId[], tagIds?: number[]) => {
    const logger = Logg_createLogger(`Flow_renderExecutionPage.fetchSyncGuides`)

    return Promise.all([
        Guid_fetchSyncGuidesByIds(logger, guideIds),
        Guid_fetchSyncGuidesByTagIds(logger, tagIds),
    ]).then(flatten)
}

const fetchOriginatingReportsCount = async (scheduledInstanceId: UUID) => {
    const scheduledInstance = await IDB_scheduledInstances().get(scheduledInstanceId)
    if (!scheduledInstance?.origin) {
        return 0
    }

    const ancestorReportsCount = scheduledInstance.ancestorReports?.length || 0

    // Need to do +1 (if they can see it) because origin report is not included in scheduled instance ancestor reports.
    return ancestorReportsCount + (Todo_canCurrentUserSeeOriginatingReport(scheduledInstance.origin) ? 1 : 0)
}

const fetchReportHistory = async (deps: CommonDeps, report: Report): Promise<HistoricalReport[]> => {
    const logger = Logg_createLogger('Flow_renderExecutionPage.fetchReportHistory')

    if (report.scheduledInstanceId) {
        const scheduledInstance: Todo_ScheduledInstanceDPO = await IDB_scheduledInstances().get(
            report.scheduledInstanceId
        )
        if (scheduledInstance && Todo_isSimpleAction(scheduledInstance)) {
            const actionReport: Flow_ActionReport = await IDB_flowActionReports().get(report.id)
            const origin = actionReport.scheduledInstance?.origin?.report
            if (origin == null) {
                return []
            }
            return Flow_fetchReportHistory(deps, actionReport.scheduledInstance?.origin?.report)
        }
    }

    const origin = await Flow_fetchReport(logger, report.id).then((r) => r?.origin)

    if (origin == null) {
        return []
    }

    return Flow_fetchReportHistory(deps, origin)
}

export const fetchGuideReportId = (
    deps: CommonDeps,
    guide: WorkflowAppGuide,
    parentClientId?: FlowReportId
): Promise<FlowReportId> =>
    Guid_fetchWorkflowGuideReportId(
        deps.createLogger('Flow_renderExecutionPage.fetchGuideReportId'),
        guide.workflow,
        parentClientId
    )

function removeMaybeFaultyQuery() {
    Rout_replace(Rout_getPath().split('?')[0])
}

function mapToSubtaskReport(subtask: Flow_FetchedReport): SubtaskReport {
    return {
        id: subtask.id,
        startDate: subtask.startDate,
        status: subtask.status,
        reportTitle: subtask.title,
        procedureName: subtask.procedure.name,
        workflowName: subtask.workflow.name,
        subjectUnitName: subtask.subjectUnit.name,
    }
}
